'use client';

import { useEffect } from 'react';

/**
 * Injects Strapi "Custom CSS" overrides AFTER the page has painted (post-LCP),
 * so they never block the critical rendering path (PSI "render-blocking"). The
 * base styling lives in globals.css (bundled); this is only for editor overrides,
 * which are cosmetic and safe to apply a tick late.
 */
export default function DeferredCustomCSS({ css, cssId }: { css: string; cssId: string }) {
  useEffect(() => {
    if (!css) return;
    const attr = 'data-strapi-custom-css';
    const inject = () => {
      const existing = document.querySelector(`style[${attr}="${cssId}"]`);
      if (existing) {
        existing.textContent = css;
        return;
      }
      const style = document.createElement('style');
      style.setAttribute(attr, cssId);
      style.textContent = css;
      document.head.appendChild(style);
    };
    // Wait for idle (after LCP); fall back to a short timeout where unsupported.
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
    const id = ric ? ric(inject) : window.setTimeout(inject, 200);
    return () => {
      const cancel = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
      if (ric && cancel) cancel(id as number);
      else clearTimeout(id as number);
    };
  }, [css, cssId]);

  return null;
}
